import type {Page} from '@PosterWhiteboard/page/page.class';
import {isImageBackground} from '@PosterWhiteboard/page/background/background.types';
import {isUserPremium} from '@Libraries/user.library';
import type {Group, TextProps} from '@postermywall/fabricjs-2';
import {FabricText, Shadow, util} from '@postermywall/fabricjs-2';
import type {LoadSVGDataResponse} from '@Utils/fabric.util';
import {loadSVGDataFromURL} from '@Utils/fabric.util';

const VIDEO_LARGE_WATERMARK_WIDTH_RATIO = 0.6;
const VIDEO_LARGE_WATERMARK_OPACITY = 0.57;

let cachedVideoLargeWatermarkSvgData: Promise<LoadSVGDataResponse> | undefined;

const getVideoLargeWatermarkSvgData = (): Promise<LoadSVGDataResponse> => {
  cachedVideoLargeWatermarkSvgData ??= loadSVGDataFromURL(window.PMW.util.asset_url('images/generation/video-watermark.svg')).catch((err: unknown) => {
    cachedVideoLargeWatermarkSvgData = undefined;
    throw err;
  });
  return cachedVideoLargeWatermarkSvgData;
};

interface WatermarkOpts {
  mode?: PAGE_WATERMARK_MODE;
  enlargeWatermark?: boolean;
  showPMW?: boolean;
}

export enum PAGE_WATERMARK_MODE {
  NONE = 'none',
  DEFAULT = 'normal',
  LARGE = 'large', // the watermark is rendered in large font across the center of the poster,
  VIDEO_LARGE = 'videolarge', // the watermark is rendered in large near the bottom with background and mentioned that this video is created using postermywall
}

const DEFAULT_WATERMARK_FONT_SIZE = 11;

export class PageWatermark {
  public page: Page;
  private videoLargeWatermarkFabricObject?: Group;
  private videoLargeWatermarkLoadPromise?: Promise<void>;
  private watermarkFabricObject?: FabricText;
  private watermarkAttrFabricObject?: FabricText;

  constructor(page: Page) {
    this.page = page;
  }

  public refreshWatermark = (): void => {
    if (this.page.poster.isHighRes) {
      void this.setWatermark({
        mode: PAGE_WATERMARK_MODE.NONE,
      });
    } else {
      void this.page.pageWatermark.setWatermark({
        mode: PAGE_WATERMARK_MODE.DEFAULT,
        showPMW: false,
      });
    }
  };

  public async setWatermark({mode = PAGE_WATERMARK_MODE.DEFAULT, enlargeWatermark = false, showPMW = true}: WatermarkOpts): Promise<void> {
    if (mode === PAGE_WATERMARK_MODE.VIDEO_LARGE) {
      await this.showLargeVideoWatermark();
      return;
    }

    if (this.videoLargeWatermarkFabricObject) {
      this.page.fabricCanvas.remove(this.videoLargeWatermarkFabricObject);
      this.videoLargeWatermarkFabricObject = undefined;
      this.videoLargeWatermarkLoadPromise = undefined;
    }

    let text = '';
    const showPosterMyWall = isUserPremium() ? false : showPMW;
    let attrText = '';
    let linesCount = 1;
    const smallFontSize = Math.max(Math.floor(this.page.poster.width / 54), DEFAULT_WATERMARK_FONT_SIZE);
    let fontSize = smallFontSize;
    let attrFontSize = smallFontSize;
    let textAlign: TextProps['textAlign'] = 'left';
    let topOffset;
    let attrTopOffset;
    const defaults = {
      fill: '#000000',
      opacity: 0.7,
      fontFamily: 'sans-serif',
      selectable: false,
      hasControls: false,
      hasBorders: false,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
    };

    if (!this.page.poster.isHighRes && mode !== PAGE_WATERMARK_MODE.NONE) {
      /**
       * Can have up to 3 lines in watermark at design time:
       * 1. PosterMyWall.com
       * 2. Premium Content
       * 3. Attribution name
       */
      if (showPosterMyWall) {
        if (enlargeWatermark) {
          fontSize *= 2.2;
        }
        text = 'Made with PosterMyWall.com';
      } else {
        text = '';
        linesCount -= 1;
      }

      if (this.page.poster.creator != null) {
        if (this.page.poster.creator.watermark !== undefined) {
          text = this.page.poster.creator.watermark;
        }
      } else if (window.PMW.currentUserOnPageLoad?.watermark) {
        text = window.PMW.currentUserOnPageLoad.watermark;
      }

      if (this.showGettyWatermark()) {
        if (text.length > 0) {
          text += '\nGetty Stock';
        } else {
          text = 'Getty Stock';
        }
        linesCount += 1;
      }

      const activeBg = this.page.items.getActiveBackground();
      if (isImageBackground(activeBg) && activeBg.requiresAttribution()) {
        attrText = this.getAttribution(activeBg.attributionName);
      }
    }

    let watermarkChangeFactor = this.page.poster.height / 30;
    let attrChangeFactor = this.page.poster.height / 45;

    if (this.page.poster.width < this.page.poster.height) {
      watermarkChangeFactor = this.page.poster.width / 30;
      attrChangeFactor = this.page.poster.width / 45;
    }

    if (mode === PAGE_WATERMARK_MODE.LARGE) {
      fontSize = smallFontSize + watermarkChangeFactor;
      attrFontSize = smallFontSize + attrChangeFactor;
      textAlign = 'center';
    }

    // have advert watermark leave space after itself for the attribution watermark
    if (attrText.length > 0) {
      linesCount += 1;
    }
    let extraTranslation = this.page.poster.height / 70;
    if (textAlign === 'center') {
      extraTranslation = this.page.poster.height / 10;
    }
    topOffset = Math.ceil(this.page.poster.height - (linesCount * fontSize * (linesCount > 1 ? 1.25 : 1) + extraTranslation));
    attrTopOffset = this.page.poster.height - (fontSize + extraTranslation);

    if (mode === PAGE_WATERMARK_MODE.LARGE) {
      topOffset = Math.ceil(this.page.poster.height / 2 - (linesCount * fontSize * (linesCount > 1 ? 1.25 : 1)) / 2);
      attrTopOffset = topOffset + (linesCount - 1) * fontSize * (linesCount > 1 ? 1.25 : 1);
    }

    if (!this.watermarkFabricObject) {
      // only initialize if we need to show a watermark
      if (text.length > 0) {
        const advertOptions = {
          top: topOffset,
          left: 10,
          fontSize,
          lineHeight: 1.2,
          textAlign,
          padding: -5,
          ...defaults,
        };

        this.watermarkFabricObject = new FabricText(text, advertOptions);
        this.watermarkFabricObject.set(
          'shadow',
          new Shadow({
            color: '#fff',
            blur: 2,
            offsetX: 0,
            offsetY: 0,
          })
        );

        this.page.fabricCanvas.add(this.watermarkFabricObject);
      }
    } else {
      this.watermarkFabricObject.set({
        text,
        fontSize,
        top: topOffset,
        left: 10,
        textAlign,
      });
    }

    if (!this.watermarkAttrFabricObject) {
      if (attrText.length > 0) {
        const attrOptions = {
          top: attrTopOffset,
          left: 10,
          fontSize: attrFontSize,
          lineHeight: 1.2,
          textAlign,
          ...defaults,
        };

        this.watermarkAttrFabricObject = new FabricText(attrText, attrOptions);
        this.page.fabricCanvas.add(this.watermarkAttrFabricObject);
      }
    } else {
      this.watermarkAttrFabricObject.set({
        text: attrText,
        fontSize: attrFontSize,
        top: attrTopOffset,
        left: 10,
        textAlign,
      });
    }

    if (textAlign === 'center') {
      if (this.watermarkFabricObject) {
        this.watermarkFabricObject.set({
          // in case the watermark is wider than the poster, have it start from 10 pixels
          left: Math.max(Math.round((this.page.poster.width - (this.watermarkFabricObject.width ?? 0)) / 2), 10),
        });
      }
      if (this.watermarkAttrFabricObject) {
        this.watermarkAttrFabricObject.set({
          // in case the watermark is wider than the poster, have it start from 10 pixels
          left: Math.max(Math.round((this.page.poster.width - (this.watermarkAttrFabricObject.width ?? 0)) / 2), 10),
        });
      }
    }

    this.page.fabricCanvas.requestRenderAll();
  }

  showLargeVideoWatermark = async (): Promise<void> => {
    this.watermarkFabricObject?.set({
      text: '',
    });
    this.watermarkAttrFabricObject?.set({
      text: '',
    });

    this.videoLargeWatermarkLoadPromise ??= this.loadAndApplyLargeVideoWatermark().catch((err: unknown) => {
      this.videoLargeWatermarkLoadPromise = undefined;
      throw err;
    });
    await this.videoLargeWatermarkLoadPromise;
  };

  private loadAndApplyLargeVideoWatermark = async (): Promise<void> => {
    const svgData = await getVideoLargeWatermarkSvgData();
    const svgGroup = util.groupSVGElements(svgData.paths, svgData.metaData) as Group;

    const canvasWidth = this.page.getNonScaledCanvasScaleddWidth();
    const canvasHeight = this.page.getNonScaledCanvasScaleddHeight();
    const targetWidth = Math.min(canvasWidth, canvasHeight) * VIDEO_LARGE_WATERMARK_WIDTH_RATIO;
    svgGroup.scaleToWidth(targetWidth);

    svgGroup.set({
      left: (canvasWidth - svgGroup.getScaledWidth()) / 2,
      top: (canvasHeight - svgGroup.getScaledHeight()) / 2,
      opacity: VIDEO_LARGE_WATERMARK_OPACITY,
      selectable: false,
      evented: false,
    });

    this.videoLargeWatermarkFabricObject = svgGroup;
    this.page.fabricCanvas.add(svgGroup);
  };

  private getAttribution(name: string | null): string {
    return name ? `Background by ${name}` : '';
  }

  private showGettyWatermark(): boolean {
    return this.page.validatePremiumUserAccess();
  }
}
